"use client"

import { useState, useEffect, useRef } from "react"
import { NavLink, useNavigate } from "react-router-dom"
import { motion, AnimatePresence } from "framer-motion"
import logo from "../../../assets/logo.png"
import { FiBell, FiLogOut, FiX, FiCheckCircle } from "react-icons/fi"
import { MdOutlineFaceUnlock } from "react-icons/md"
import { FaCaretDown } from "react-icons/fa"
import MenuToggleIcon from "./MenuToggleIcon"
import { useDispatch, useSelector } from "react-redux"
import socketService from "../../../config/socketService"
import {
  addNotification,
  clearNotifications,
  fetchNotifications,
  readNotification,
  // markAllNotificationsAsRead,
} from "../../../redux/features/notificationSlice"
import { logout } from "../../../redux/features/logoutSlice"

const navItems = [
  { name: "Dashboard", path: "/client" },
  { name: "Applications", path: "/client/applications" },
  // { name: "Documents", path: "/client/documents" },
  { name: "Invoices", path: "/client/invoices" },
]

export default function ClientNavbar() {
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const [user, setUser] = useState()
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false)

  // Get notifications from Redux store
  const { notifications, isLoaded } = useSelector((state) => state.notifications)
  const [unreadCount, setUnreadCount] = useState(0)
  const [isNotificationOpen, setIsNotificationOpen] = useState(false)
  const notificationRef = useRef(null)

  useEffect(() => {
    const userData = localStorage.getItem("user")
    if (!userData) {
      navigate("/")
    } else {
      try {
        const parsedUser = JSON.parse(userData)
        if (parsedUser?.role !== "client") {
          navigate("/")
        }
        setUser(parsedUser)
      } catch (error) {
        console.error("Error parsing user data from localStorage:", error)
      }
    }
  }, [])

  useEffect(() => {
    console.log(notifications)
  }, [notifications])

  // Fetch notifications on component mount
  useEffect(() => {
    // Only fetch notifications if they haven't been loaded yet
    if (!isLoaded) {
      dispatch(fetchNotifications())
    }

    // Setup socket connection
    setupSocketConnection()

    // Close notification dropdown when clicking outside
    // const handleClickOutside = (event) => {
    //   if (notificationRef.current && !notificationRef.current.contains(event.target)) {
    //     setIsNotificationOpen(false)
    //   }
    // }

    // document.addEventListener("mousedown", handleClickOutside)

    // Proper cleanup
    // return () => {
      // document.removeEventListener("mousedown", handleClickOutside)
      // Don't disconnect the socket here, as we want to keep it alive between route changes
      // We'll only disconnect on logout
    // }
  }, [isLoaded, dispatch]) // Added dispatch to dependency array

  useEffect(() => {
    if (!isNotificationOpen) return

    const handleNotificationOutsideClick = (event) => {
      const clickedNotificationTrigger = event.target.closest('button[aria-label="Notifications"]')

      if (clickedNotificationTrigger) {
        return
      }

      if (
        notificationRef.current &&
        !notificationRef.current.contains(event.target)
      ) {
        setIsNotificationOpen(false)
      }
    }

    document.addEventListener("mousedown", handleNotificationOutsideClick)
    document.addEventListener("touchstart", handleNotificationOutsideClick)

    return () => {
      document.removeEventListener("mousedown", handleNotificationOutsideClick)
      document.removeEventListener("touchstart", handleNotificationOutsideClick)
    }
  }, [isNotificationOpen])

  // Update unread count whenever notifications change
  useEffect(() => {
    if (notifications) {
      const unreadNotifications = notifications.filter((notification) => !notification.isRead)
      setUnreadCount(unreadNotifications.length)
    }
  }, [notifications])

  const setupSocketConnection = () => {
    try {
      const userData = localStorage.getItem("user")
      if (!userData) return

      const user = JSON.parse(userData)
      const userId = user._id

      // Connect to socket server - will reuse existing connection if already connected
      socketService.connect(userId)

      // Set up notification listener
      socketService.setupNotificationListener(userId, (newNotification) => {
        console.log("+++++++++++", newNotification)
        dispatch(addNotification(newNotification.notification))
      })
    } catch (error) {
      console.error("Error setting up socket connection:", error)
    }
  }

  const handleNotificationClick = (notification) => {
    // Mark as read when clicked
    if (!notification.isRead) {
      dispatch(readNotification(notification._id))
    }

    // Navigate to relevant page if needed
    if (notification.link) {
      navigate(notification.link)
      setIsNotificationOpen(false)
    }
  }

  const toggleNotifications = () => {
    setIsNotificationOpen(!isNotificationOpen)
  }

  // const handleMarkAllAsRead = () => {
  //   dispatch(markAllNotificationsAsRead())
  // }

  const handleLogout = () => {
    localStorage.removeItem("token")
    localStorage.removeItem("user")

    dispatch(logout())
    dispatch(clearNotifications())

    socketService.disconnect()

    navigate("/")
  }

  const handleLogoClick = () => {
    navigate("/client")
  }

  return (
    <header className="bg-[#005787] text-white px-4 xl:px-6 py-4 lg:py-6 relative">
      <div className="flex items-center justify-between gap-2">
        {/* Logo */}
        <div className="flex-shrink-0">
          <button
            type="button"
            onClick={handleLogoClick}
            className="cursor-pointer"
            aria-label="Go to dashboard"
          >
            <img
              loading={"lazy"}
              src={logo || "/placeholder.svg"}
              alt="World Class Logo"
              width={110}
              height={40}
              className="xl:w-[120px]"
            />
          </button>
        </div>

        {/* Desktop Navigation */}
        <nav className="hidden lg:flex flex-1 min-w-0 justify-center">
          <ul className="flex flex-wrap justify-center items-center gap-x-2 xl:gap-x-4 gap-y-1">
            {navItems.map((item) => (
              <li key={item.name} className="flex-shrink-0">
                <NavLink
                  to={item.path}
                  end={item.path === "/client"}
                  className={({ isActive }) =>
                    `cursor-pointer transition-colors duration-200 relative pb-1 text-[13px] xl:text-sm whitespace-nowrap ${
                      isActive
                        ? "text-white after:absolute after:bottom-0 after:left-1/2 after:-translate-x-1/2 after:w-[40px] after:h-[3px] after:bg-[#87CA60] after:rounded-sm"
                        : ""
                    }`
                  }
                >
                  {item.name}
                </NavLink>
              </li>
            ))}
          </ul>
        </nav>

        {/* Desktop Right Section */}
        <div className="hidden lg:flex items-center gap-2 xl:gap-3 flex-shrink-0">
          <div className="relative flex-shrink-0" data-notification-trigger="true">
            <div className="relative">
              <button className="relative focus:outline-none" onClick={toggleNotifications} aria-label="Notifications">
                <FiBell className="cursor-pointer text-[#87CA60] h-5 w-5 xl:h-6 xl:w-6" strokeWidth={3} />
                {unreadCount > 0 && (
                  <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full h-4 w-4 flex items-center justify-center">
                    {unreadCount}
                  </span>
                )}
              </button>
            </div>
          </div>
          <div className="relative group text-slate-800 flex-shrink-0">
            <div className="flex items-center gap-1.5 bg-[#87CA60] px-2 py-1.5 rounded-full cursor-pointer">
              <MdOutlineFaceUnlock className="h-5 w-5 xl:h-6 xl:w-6 bg-white text-slate-700 rounded-full flex-shrink-0" />
              <span className="font-medium text-xs xl:text-sm whitespace-nowrap max-w-[80px] xl:max-w-[110px] truncate">Hello {user?.firstName ? user.firstName : "Client"}</span>
              <FaCaretDown className="text-white w-3.5 h-3.5 flex-shrink-0" />
            </div>

            {/* Dropdown */}
            <div className="absolute z-50 right-0 w-48 bg-transparent pt-2">
              <div className="bg-[#87CA60] shadow-lg rounded-lg hidden group-hover:block">
                <ul className="py-2">
                  <li
                    onClick={() => navigate("/client/settings")}
                    className="px-4 py-2 hover:bg-gray-100/10 cursor-pointer"
                  >
                    Settings
                  </li>
                  <li onClick={handleLogout} className="px-4 py-2 hover:bg-gray-100/10 cursor-pointer">
                    Logout
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>

        {/* Mobile Menu Button */}
        <div className="lg:hidden flex items-center space-x-4 flex-shrink-0">
          <div className="relative mt-2" >
            <button className="relative focus:outline-none" onClick={toggleNotifications} aria-label="Notifications">
              <FiBell className="cursor-pointer text-[#87CA60] h-6 w-6" strokeWidth={3} />
              {unreadCount > 0 && (
                <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full h-4 w-4 flex items-center justify-center">
                  {unreadCount}
                </span>
              )}
            </button>
          </div>
          <MenuToggleIcon isOpen={isMobileMenuOpen} setIsOpen={setIsMobileMenuOpen} />
        </div>
      </div>

      {/* Mobile Menu */}
      <AnimatePresence>
        {isMobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.3 }}
            className="lg:hidden overflow-hidden"
          >
            <div className="mt-4 pb-4">
              <motion.div
                initial={{ x: -20, opacity: 0 }}
                animate={{ x: 0, opacity: 1 }}
                transition={{ delay: 0.1 }}
                className="flex items-center space-x-2 mb-4"
              ></motion.div>
              <nav>
                <ul className="space-y-2">
                  {navItems.map((item, index) => (
                    <motion.li
                      key={item.name}
                      initial={{ x: -20, opacity: 0 }}
                      animate={{ x: 0, opacity: 1 }}
                      transition={{ delay: index * 0.1 }}
                    >
                      <NavLink
                        to={item.path}
                        end={item.path === "/client"}
                        className={({ isActive }) =>
                          `block hover:bg-white/10 px-2 py-1 rounded cursor-pointer relative ${
                            isActive
                              ? "bg-white/10 text-white font-medium after:absolute after:bottom-0 after:left-2 after:right-2 after:h-0.5 after:bg-white"
                              : ""
                          }`
                        }
                        onClick={() => setIsMobileMenuOpen(false)}
                      >
                        {item.name}
                      </NavLink>
                    </motion.li>
                  ))}
                  <motion.li
                    key={"Settings"}
                    initial={{ x: -20, opacity: 0 }}
                    animate={{ x: 0, opacity: 1 }}
                    transition={{ delay: 5 * 0.1 }}
                  >
                    <NavLink
                      to={"/client/settings"}
                      end={"/client/settings" === "/client"}
                      className={({ isActive }) =>
                        `block hover:bg-white/10 px-2 py-1 rounded cursor-pointer relative ${
                          isActive
                            ? "bg-white/10 text-white font-medium after:absolute after:bottom-0 after:left-2 after:right-2 after:h-0.5 after:bg-white"
                            : ""
                        }`
                      }
                      onClick={() => setIsMobileMenuOpen(false)}
                    >
                      {"Settings"}
                    </NavLink>
                  </motion.li>
                </ul>
              </nav>
              <motion.div
                initial={{ y: 20, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ delay: 0.3 }}
                className="flex items-center justify-between mt-4 pt-4 border-t border-white/20"
              >
                <div className="flex items-center space-x-2">
                  <span>Hello {user?.firstName ? user.firstName : "Client"}</span>
                  <MdOutlineFaceUnlock className="h-6 w-6 bg-white text-slate-700 rounded-full" />
                </div>
                <button
                  onClick={handleLogout}
                  className="bg-white text-red-500 flex flex-row items-center gap-3 p-2 rounded-lg"
                >
                  <FiLogOut className="w-5 h-5" />
                  <span>Logout</span>
                </button>
              </motion.div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Notification Panel */}
      <AnimatePresence>
        {isNotificationOpen && (
          <motion.div
            ref={notificationRef}
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.2 }}
            className="fixed top-[80px] right-4 md:right-6 lg:absolute lg:top-full lg:right-0 w-[90%] md:w-96 bg-[#FAF6F1] border border-gray-200 rounded-lg shadow-lg z-[1000] max-h-[80vh] flex flex-col"
            style={{ maxHeight: 'calc(100vh - 200px)' }}
          >
            <div className="p-4 border-b border-gray-200 bg-[#005787] text-white flex items-center justify-between">
              <h3 className="text-lg font-medium">Notifications</h3>
              <div className="flex items-center space-x-3">
                {/* {notifications && notifications.some((n) => !n.isRead) && (
                  <button
                    // onClick={handleMarkAllAsRead}
                    className="flex items-center text-sm hover:text-[#87CA60] transition-colors"
                  >
                    <FiCheckCircle className="mr-1" />
                    <span className="hidden sm:inline">Mark all as read</span>
                  </button>
                )} */}
                <button
                  onClick={() => setIsNotificationOpen(false)}
                  className="p-1 rounded-full hover:bg-[#87CA60] transition-colors"
                  aria-label="Close notifications"
                >
                  <FiX className="w-5 h-5" />
                </button>
              </div>
            </div>
            <div className="overflow-y-auto flex-1">
              {notifications && notifications.length > 0 ? (
                <div className="divide-y divide-gray-100">
                  {notifications.map((notification) => (
                    <div
                      key={notification._id}
                      className={`p-4 cursor-pointer hover:bg-gray-50 transition-colors ${
                        !notification.isRead ? "bg-blue-50" : ""
                      }`}
                      onClick={() => handleNotificationClick(notification)}
                    >
                      <div className="flex items-start">
                        {!notification.isRead && (
                          <span className="h-2 w-2 mt-1.5 rounded-full bg-blue-500 mr-2 flex-shrink-0" />
                        )}
                        <div className="flex-1">
                          <p className="text-sm text-gray-800">{notification.message}</p>
                          <p className="text-xs text-gray-500 mt-1">
                            {new Date(notification.createdAt).toLocaleString()}
                          </p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="p-8 text-center text-gray-500">No notifications</div>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  )
}
